import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { Globe, ArrowRight } from "lucide-react";

const points = [
  "A professional website built for your trucking company",
  "Your equipment, lanes, and services showcased to brokers and shippers",
  "Instant credibility — you're searchable, findable, and verifiable",
  "Built, hosted, and maintained by us — available only to our carriers",
];

export const DigitalPresence = () => (
  <section id="digital" className="py-28 sm:py-36 overflow-hidden" data-testid="digital-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
      <div>
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-neutral-500 mb-4">
            Beyond Dispatch — The Purpose Standard
          </p>
          <h2 className="font-display font-bold uppercase tracking-tight leading-[0.95] text-3xl sm:text-5xl lg:text-6xl mb-6">
            We keep you moving.
            <br />
            <span className="text-neutral-500">Then we make you visible.</span>
          </h2>
          <p className="text-neutral-400 leading-relaxed max-w-lg mb-8">
            Any dispatcher can find you freight. Nobody else builds your business while they do it.
            Carriers that partner with Purpose Haul can add something no other dispatcher offers —
            a professional website for your company, built and maintained by us — so brokers and
            shippers see a carrier they can trust before you ever answer the phone.
          </p>
        </Reveal>
        <ul className="space-y-0">
          {points.map((p, i) => (
            <Reveal key={p} delay={0.1 + i * 0.08}>
              <li className="flex items-center gap-4 py-4 border-b border-white/10 text-sm text-neutral-300" data-testid={`digital-point-${i}`}>
                <ArrowRight size={14} className="text-white shrink-0" />
                {p}
              </li>
            </Reveal>
          ))}
        </ul>
        <Reveal delay={0.4}>
          <div className="mt-10 grid sm:grid-cols-2 gap-3" data-testid="digital-pricing">
            <div className="border border-white/15 p-6 hover:bg-white/[0.03] transition-colors duration-500">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">Launch Site</p>
              <p className="font-display font-extrabold text-3xl mt-2" data-testid="digital-price-launch">
                $199<span className="text-sm font-normal text-neutral-500"> one-time</span>
              </p>
              <p className="text-neutral-400 text-sm mt-3 leading-relaxed">
                A single-page professional site: your story, equipment, lanes, and a contact form for brokers and shippers.
              </p>
            </div>
            <div className="border border-white/40 bg-white/[0.04] p-6 hover:bg-white/[0.07] transition-colors duration-500">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400">Pro Site</p>
              <p className="font-display font-extrabold text-3xl mt-2" data-testid="digital-price-pro">
                $399<span className="text-sm font-normal text-neutral-500"> + $29/mo</span>
              </p>
              <p className="text-neutral-400 text-sm mt-3 leading-relaxed">
                A multi-section site with your own domain, plus hosting, updates, and maintenance handled for you.
              </p>
            </div>
          </div>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            Offered exclusively to our partnered carriers. No other dispatcher does this.
          </p>
        </Reveal>
      </div>

      <Reveal delay={0.2}>
        <motion.div
          whileHover={{ y: -6 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="border border-white/15 bg-[#101010]"
          data-testid="digital-mockup"
        >
          <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
            <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
            <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
            <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
            <span className="ml-3 font-mono text-[10px] tracking-[0.15em] text-neutral-500 flex items-center gap-1.5">
              <Globe size={11} /> yourcompany.com
            </span>
          </div>
          <div className="p-6 sm:p-8">
            <div className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl leading-none">
              Your Company
              <span className="block text-white/25">Transport LLC</span>
            </div>
            <div className="mt-3 h-2 w-2/3 bg-white/10" />
            <div className="mt-2 h-2 w-1/2 bg-white/10" />
            <div className="mt-6 grid grid-cols-3 gap-2">
              <div className="h-16 bg-white/5 border border-white/10" />
              <div className="h-16 bg-white/5 border border-white/10" />
              <div className="h-16 bg-white/5 border border-white/10" />
            </div>
            <div className="mt-6 flex gap-2">
              <div className="h-8 w-24 bg-white" />
              <div className="h-8 w-24 border border-white/25" />
            </div>
          </div>
        </motion.div>
      </Reveal>
    </div>
  </section>
);
